import { type ConfigObject, InfoGridFuelCategory } from '../../../../_configs/types';
import type { Car, CarVerbrauchParsed, CarVerbrauchParsedNumbered } from '../../../../_types/Car';
import { effizienzSteps } from '../../../../components/effizienz/steps';
import { EffizienzClass } from '../../../../components/effizienz/types';
import checkUndefinedNullOrEmpty from '../../../../utils/tools/checkUndefinedNullOrEmpty';

const getValue = (field: number, fractionDigits: number, prefix: string): string => {
    return !checkUndefinedNullOrEmpty(field)
        ? `${!checkUndefinedNullOrEmpty(prefix) ? `${prefix} ` : ''}${field?.toLocaleString('de-DE', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })}`
        : '-';
};

export default function prepareCarData(
    car: Car<true>,
    settings: ConfigObject
): {
    bilderUrl: Car<false>['bilder']['url'];
    bilderUrlHD: Car<false>['bilder']['urlHD'];
    bilderUrlS: Car<false>['bilder']['urlS'];
    usersSelect: Car<false>['usersSelect'];
    verbrauch: Car<false>['verbrauch'];
} {
    const usersSelect = [];
    if (!checkUndefinedNullOrEmpty(car?.users)) {
        car?.users?.map(function (u) {
            if (!checkUndefinedNullOrEmpty(u) && !checkUndefinedNullOrEmpty(u.i)) {
                const i = u.i || '-1';
                const idf = u.idf || '-1';
                const vn = u.vn || '';
                const nn = u.nn || '';
                usersSelect.push({
                    i: i,
                    t: [vn, nn].join(' '),
                    avatar: 'https://cars.autohaus-digital.de/r_images/s/v1/firma/' + idf + '/' + i + '/user.jpg',
                    avatarFallback: [vn?.substring(0, 1), nn?.substring(0, 1)],
                });
            }
        });
    }

    const wltp = car?.engine?.wltp || car?.wltp;
    let verbrauch: CarVerbrauchParsed = undefined;
    const engineMotor = car?.engine?.motor || '';
    // const biFuel = engineMotor?.startsWith('Bi Fuel');
    const elektro = engineMotor === 'Elektro';
    const gasantrieb = engineMotor === 'Autogas (LPG)' || engineMotor === 'Erdgas (CNG)';
    const hybrid = engineMotor?.startsWith('Hybrid');
    const pluginHybrid = engineMotor.startsWith('Plug-In-Hybrid');
    const wasserstoff = engineMotor.startsWith('Wasserstoff');
    const ks1 = car?.engine?.kraftstoff || '';
    const ks2 = car?.engine?.kraftstoff_1 || '';

    if (!checkUndefinedNullOrEmpty(wltp)) {
        const einheiten = [
            ks1 === 'Strom' || elektro ? 'kWh/100km' : ks1.includes('Erdgas') || ks1.includes('Autogas') ? 'kg/100km' : 'l/100km',
            ks2 === 'Strom' || pluginHybrid ? 'kWh/100km' : ks2.includes('Erdgas') || ks2.includes('Autogas') ? 'kg/100km' : 'l/100km',
        ];
        const commonCarVerbrauchParsed: Pick<CarVerbrauchParsed, 'euronorm' | 'envkv' | 'fuelCategory' | 'motor'> = {
            envkv: car?.engine?.envkv,
            euronorm: !elektro ? car?.engine?.schadstoff || '-' : undefined,
            fuelCategory: wasserstoff
                ? InfoGridFuelCategory.CELL
                : pluginHybrid
                  ? InfoGridFuelCategory.PLUGINHYBRID
                  : elektro
                    ? InfoGridFuelCategory.ELECTRO
                    : gasantrieb
                      ? InfoGridFuelCategory.GAS
                      : InfoGridFuelCategory.DEFAULT,
            motor: !checkUndefinedNullOrEmpty(engineMotor) ? engineMotor : '-',
        };
        const verbrauchNumbered: CarVerbrauchParsedNumbered = {
            combined: elektro ? wltp?.w_cpc : pluginHybrid ? wltp?.w_cfcw : wltp?.w_cfc,
            combinedDischarged: pluginHybrid ? wltp?.w_cfcDischarged : undefined,
            combinedPrimary: hybrid || pluginHybrid ? wltp?.w_cfcw : hybrid ? wltp?.w_cfc : undefined,
            combinedSecondary: hybrid || pluginHybrid ? wltp?.w_cpcw : hybrid ? wltp?.w_cpc : undefined,
            low: elektro || pluginHybrid ? wltp?.w_cpl : wltp?.w_cfl,
            lowDischarged: pluginHybrid ? wltp?.w_cfl : undefined,
            medium: elektro || pluginHybrid ? wltp?.w_cpm : wltp?.w_cfm,
            mediumDischarged: pluginHybrid ? wltp?.w_cfm : undefined,
            high: elektro || pluginHybrid ? wltp?.w_cph : wltp?.w_cfh,
            highDischarged: pluginHybrid ? wltp?.w_cfh : undefined,
            extraHigh: elektro || pluginHybrid ? wltp?.w_cpeh : wltp?.w_cfeh,
            extraHighDischarged: pluginHybrid ? wltp?.w_cfeh : undefined,
            emission: pluginHybrid ? wltp?.w_co2ecw : wltp?.w_co2ec,
            emissionDischarged: pluginHybrid ? wltp?.w_co2ecDischarged : undefined,
            range: pluginHybrid || elektro ? wltp?.w_er : undefined,
            effizienz: (effizienzSteps?.find((e) => e?.t === (pluginHybrid ? wltp?.co2classWeighted : wltp?.co2class))?.i as EffizienzClass) || EffizienzClass.N_A,
            effizienzDischarged: pluginHybrid ? (effizienzSteps?.find((e) => e?.t === wltp?.co2classDischarged)?.i as EffizienzClass) || EffizienzClass.N_A : undefined,
            energycosts: wltp?.a_costs15,
            co2costsLow: wltp?.co2costslow,
            co2costsMedium: wltp?.co2costsmedium,
            co2costsHigh: wltp?.co2costshigh,
        };
        verbrauch = {
            ...commonCarVerbrauchParsed,
            combined: !checkUndefinedNullOrEmpty(verbrauchNumbered?.combined)
                ? [getValue(verbrauchNumbered?.combined, 1, car?.engine?.envkv === true ? undefined : 'ca.'), einheiten[0]].join(' ')
                : undefined,
            combinedDischarged: !checkUndefinedNullOrEmpty(verbrauchNumbered?.combinedDischarged)
                ? [getValue(verbrauchNumbered?.combinedDischarged, 1, car?.engine?.envkv === true ? undefined : 'ca.'), einheiten[0]].join(' ')
                : undefined,
            combinedPrimary: !checkUndefinedNullOrEmpty(verbrauchNumbered?.combinedPrimary)
                ? [getValue(verbrauchNumbered?.combinedPrimary, 1, car?.engine?.envkv === true ? undefined : 'ca.'), einheiten[0]].join(' ')
                : undefined,
            combinedSecondary: !checkUndefinedNullOrEmpty(verbrauchNumbered?.combinedSecondary)
                ? [getValue(verbrauchNumbered?.combinedSecondary, 1, car?.engine?.envkv === true ? undefined : 'ca.'), einheiten[1]].join(' ')
                : undefined,
            low: !checkUndefinedNullOrEmpty(verbrauchNumbered?.low)
                ? [getValue(verbrauchNumbered?.low, 1, car?.engine?.envkv === true ? undefined : 'ca.'), einheiten[pluginHybrid ? 1 : 0]].join(' ')
                : undefined,
            lowDischarged: !checkUndefinedNullOrEmpty(verbrauchNumbered?.lowDischarged)
                ? [getValue(verbrauchNumbered?.lowDischarged, 1, car?.engine?.envkv === true ? undefined : 'ca.'), einheiten[0]].join(' ')
                : undefined,
            medium: !checkUndefinedNullOrEmpty(verbrauchNumbered?.medium)
                ? [getValue(verbrauchNumbered?.medium, 1, car?.engine?.envkv === true ? undefined : 'ca.'), einheiten[pluginHybrid ? 1 : 0]].join(' ')
                : undefined,
            mediumDischarged: !checkUndefinedNullOrEmpty(verbrauchNumbered?.mediumDischarged)
                ? [getValue(verbrauchNumbered?.mediumDischarged, 1, car?.engine?.envkv === true ? undefined : 'ca.'), einheiten[0]].join(' ')
                : undefined,
            high: !checkUndefinedNullOrEmpty(verbrauchNumbered?.high)
                ? [getValue(verbrauchNumbered?.high, 1, car?.engine?.envkv === true ? undefined : 'ca.'), einheiten[pluginHybrid ? 1 : 0]].join(' ')
                : undefined,
            highDischarged: !checkUndefinedNullOrEmpty(verbrauchNumbered?.highDischarged)
                ? [getValue(verbrauchNumbered?.highDischarged, 1, car?.engine?.envkv === true ? undefined : 'ca.'), einheiten[0]].join(' ')
                : undefined,
            extraHigh: !checkUndefinedNullOrEmpty(verbrauchNumbered?.extraHigh)
                ? [getValue(verbrauchNumbered?.extraHigh, 1, car?.engine?.envkv === true ? undefined : 'ca.'), einheiten[pluginHybrid ? 1 : 0]].join(' ')
                : undefined,
            extraHighDischarged: !checkUndefinedNullOrEmpty(verbrauchNumbered?.extraHighDischarged)
                ? [getValue(verbrauchNumbered?.extraHighDischarged, 1, car?.engine?.envkv === true ? undefined : 'ca.'), einheiten[0]].join(' ')
                : undefined,
            emission: !checkUndefinedNullOrEmpty(verbrauchNumbered?.emission)
                ? [getValue(verbrauchNumbered?.emission, 0, car?.engine?.envkv === true ? undefined : 'ca.'), 'g/km'].join(' ')
                : undefined,
            emissionDischarged: !checkUndefinedNullOrEmpty(verbrauchNumbered?.emissionDischarged)
                ? [getValue(verbrauchNumbered?.emissionDischarged, 0, car?.engine?.envkv === true ? undefined : 'ca.'), 'g/km'].join(' ')
                : undefined,
            range: !checkUndefinedNullOrEmpty(verbrauchNumbered?.range) ? [getValue(verbrauchNumbered?.range, 0, car?.engine?.envkv === true ? undefined : 'ca.'), 'km'].join(' ') : undefined,
            effizienz: !checkUndefinedNullOrEmpty(verbrauchNumbered?.effizienz) ? verbrauchNumbered?.effizienz : undefined,
            effizienzDischarged: !checkUndefinedNullOrEmpty(verbrauchNumbered?.effizienzDischarged) ? verbrauchNumbered?.effizienzDischarged : undefined,
            energycosts: !checkUndefinedNullOrEmpty(verbrauchNumbered?.energycosts)
                ? [getValue(verbrauchNumbered?.energycosts, 2, car?.engine?.envkv === true ? undefined : 'ca.'), '€'].join(' ')
                : undefined,
            co2costsLow: !checkUndefinedNullOrEmpty(verbrauchNumbered?.co2costsLow)
                ? [getValue(verbrauchNumbered?.co2costsLow, 2, car?.engine?.envkv === true ? undefined : 'ca.'), '€'].join(' ')
                : undefined,
            co2costsMedium: !checkUndefinedNullOrEmpty(verbrauchNumbered?.co2costsMedium)
                ? [getValue(verbrauchNumbered?.co2costsMedium, 2, car?.engine?.envkv === true ? undefined : 'ca.'), '€'].join(' ')
                : undefined,
            co2costsHigh: !checkUndefinedNullOrEmpty(verbrauchNumbered?.co2costsHigh)
                ? [getValue(verbrauchNumbered?.co2costsHigh, 2, car?.engine?.envkv === true ? undefined : 'ca.'), '€'].join(' ')
                : undefined,
        };
    } else {
        verbrauch = {
            envkv: car?.engine?.envkv,
            euronorm: !elektro ? car?.engine?.schadstoff || '-' : undefined,
            fuelCategory: wasserstoff
                ? InfoGridFuelCategory.CELL
                : pluginHybrid
                  ? InfoGridFuelCategory.PLUGINHYBRID
                  : elektro
                    ? InfoGridFuelCategory.ELECTRO
                    : gasantrieb
                      ? InfoGridFuelCategory.GAS
                      : InfoGridFuelCategory.DEFAULT,
            motor: !checkUndefinedNullOrEmpty(engineMotor) ? engineMotor : '-',
            co2costsHigh: undefined,
            co2costsLow: undefined,
            co2costsMedium: undefined,
            combined: undefined,
            combinedDischarged: undefined,
            combinedPrimary: undefined,
            combinedSecondary: undefined,
            effizienz: undefined,
            effizienzDischarged: undefined,
            emission: undefined,
            emissionDischarged: undefined,
            energycosts: undefined,
            extraHigh: undefined,
            extraHighDischarged: undefined,
            high: undefined,
            highDischarged: undefined,
            low: undefined,
            lowDischarged: undefined,
            medium: undefined,
            mediumDischarged: undefined,
            range: undefined,
        };
    }

    const bilderUrl =
        car?.bilder?.url?.slice(
            !checkUndefinedNullOrEmpty(settings?.fahrzeugsuche?.firstImageIndex)
                ? settings?.fahrzeugsuche?.firstImageIndex
                : car?.bilder?.ec === false || (settings?.fahrzeugsuche?.useEyecatcher && car?.bilder?.ec === true)
                  ? 0
                  : 1
        ) || [];
    const bilderUrlS =
        car?.bilder?.urlS?.slice(
            !checkUndefinedNullOrEmpty(settings?.fahrzeugsuche?.firstImageIndex)
                ? settings?.fahrzeugsuche?.firstImageIndex
                : car?.bilder?.ec === false || (settings?.fahrzeugsuche?.useEyecatcher && car?.bilder?.ec === true)
                  ? 0
                  : 1
        ) || [];
    const bilderUrlHD =
        car?.bilder?.urlHD?.slice(
            !checkUndefinedNullOrEmpty(settings?.fahrzeugsuche?.firstImageIndex)
                ? settings?.fahrzeugsuche?.firstImageIndex
                : car?.bilder?.ec === false || (settings?.fahrzeugsuche?.useEyecatcher && car?.bilder?.ec === true)
                  ? 0
                  : 1
        ) || [];

    return { bilderUrl, bilderUrlHD, bilderUrlS, usersSelect, verbrauch };
}
